;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/invest/:slug\",\"regex\":\"^/invest/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/invest/:slug/place-allocation\",\"regex\":\"^/invest/([^/]+)/place-allocation$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/invest/:slug/place-allocation/:step\",\"regex\":\"^/invest/([^/]+)/place-allocation/([^/]+)$\",\"paramNames\":[\"slug\",\"step\"],\"hasOptionalPrefix\":false},{\"path\":\"/invest/:slug/place-multiple-allocations\",\"regex\":\"^/invest/([^/]+)/place-multiple-allocations$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/invest/:slug/place-multiple-allocations/:step\",\"regex\":\"^/invest/([^/]+)/place-multiple-allocations/([^/]+)$\",\"paramNames\":[\"slug\",\"step\"],\"hasOptionalPrefix\":false},{\"path\":\"/trade/:sellOrderId\",\"regex\":\"^/trade/([^/]+)$\",\"paramNames\":[\"sellOrderId\"],\"hasOptionalPrefix\":false},{\"path\":\"/portfolio/:slug\",\"regex\":\"^/portfolio/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/proxy-voting/:slug\",\"regex\":\"^/proxy-voting/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/set-active-investing-entity/:id\",\"regex\":\"^/set-active-investing-entity/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/notifications/verify-bank-account/:notificationId\",\"regex\":\"^/notifications/verify-bank-account/([^/]+)$\",\"paramNames\":[\"notificationId\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/activate-account\"},{\"path\":\"/auth-redirect\"},{\"path\":\"/create-account\"},{\"path\":\"/forgot-password\"},{\"path\":\"/login\"},{\"path\":\"/login/@deprecated\"},{\"path\":\"/login/verify\"},{\"path\":\"/marketing-unsubscribe\"},{\"path\":\"/set-password\"},{\"path\":\"/invest\"},{\"path\":\"/trade\"},{\"path\":\"/preferences/account-details\"},{\"path\":\"/preferences/account-settings\"},{\"path\":\"/preferences/integrations\"},{\"path\":\"/preferences/security\"},{\"path\":\"/account-locked\"},{\"path\":\"/distributions\"},{\"path\":\"/documents\"},{\"path\":\"/holding-movements\"},{\"path\":\"/investing-entities/accounts\"},{\"path\":\"/investing-entities/bank-accounts\"},{\"path\":\"/investing-entities/certificates\"},{\"path\":\"/investing-entities/details\"},{\"path\":\"/investing-entities/distributions\"},{\"path\":\"/orders\"},{\"path\":\"/portfolio\"},{\"path\":\"/switch-investing-entities\"},{\"path\":\"/add-investing-entity\"},{\"path\":\"/account-identity-verification\"},{\"path\":\"/notifications\"},{\"path\":\"/notifications/reattempt-verify-identity\"},{\"path\":\"/notifications/verify-identity\"},{\"path\":\"/notifications/add-bank-account\"},{\"path\":\"/notifications/confirm-accreditation\"},{\"path\":\"/notifications/confirm-email-address\"},{\"path\":\"/notifications/provide-investing-entity-documents\"},{\"path\":\"/notifications/provide-related-party-contact-details\"},{\"path\":\"/notifications/provide-source-of-funds\"},{\"path\":\"/notifications/reattempt-accreditation\"},{\"path\":\"/notifications/reattempt-provide-source-of-funds\"},{\"path\":\"/notifications/reattempt-verify-address\"},{\"path\":\"/notifications/renew-accreditation\"},{\"path\":\"/notifications/verify-address\"},{\"path\":\"/related-party/verify-identity\"},{\"path\":\"/error-500\"},{\"path\":\"/maintenance\"},{\"path\":\"/mock\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.2";import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import {
	posthogSessionRecordingConfig,
	sanitizePostHogEvent,
	sentryReplayIntegrationOptions,
	sentryReplaySampleRates,
} from "@jasperlabs/utils";

import {
	redactBreadcrumb,
	redactEvent,
	replayRedactionOptions,
} from "./src/lib/sentryRedaction";

/**
 * Next.js instrumentation-client.ts — runs once on the client before the React
 * tree mounts, making posthog.init() available before any component renders.
 * https://posthog.com/docs/libraries/next-js
 */
if (
	process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "1" &&
	process.env.NEXT_PUBLIC_POSTHOG_KEY
) {
	posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
		api_host:
			process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com",
		defaults: "2026-01-30",
		person_profiles: "identified_only",
		capture_pageview: false, // Manual pageview capture via PostHogPageView
		autocapture: false, // No implicit click/input capture; all events are explicit
		before_send: sanitizePostHogEvent,
		// Session recording is dev-only (project decision D8): on in caruso-dev
		// (incl. previews), off in staging/production; inputs + all text always masked.
		...posthogSessionRecordingConfig(
			process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT,
		),
	});
	posthog.register({ portal: "investor-portal" });
}

/**
 * Client-side Sentry initialisation. Migrated here from the previous
 * `@sentry/browser` init in `src/contexts/logger/logger.js` as part of the move
 * to `@sentry/nextjs` (APP-24209). Guarded on the DSN so local/dev runs without
 * a DSN stay inert. A DSN without a runtime environment is a misconfiguration
 * (the former useSentryInitialise hook threw here); surface it loudly and skip
 * init rather than throwing, since this module also bootstraps PostHog and runs
 * before the React tree mounts.
 */
if (
	process.env.NEXT_PUBLIC_SENTRY_DSN &&
	!process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT
) {
	// eslint-disable-next-line no-console -- Sentry is not initialised, so the console is the only place to report this
	console.error(
		"NEXT_PUBLIC_SENTRY_DSN is set but NEXT_PUBLIC_RUNTIME_ENVIRONMENT is missing; skipping Sentry initialisation.",
	);
} else if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
	Sentry.init({
		dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
		environment: process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT,
		...(process.env.NEXT_PUBLIC_RELEASE_ID && {
			release: process.env.NEXT_PUBLIC_RELEASE_ID,
		}),
		ignoreErrors: [
			// GraphQL errors are 'unhandled' & propagate up to Sentry
			/^(GraphQL error:)/,
			/^(GraphQL Errors:)/,
			/^(TypeError: Failed to fetch)/,
			/^(Network error:)/,
		],
		// Related Party Token in the page URL (SEC-412): see src/lib/sentryRedaction.ts.
		beforeBreadcrumb: redactBreadcrumb,
		// Session Replay (APP-24207): capture a replay only when an error occurs;
		// PostHog owns full-session recording. All text/inputs/media masked by
		// default — see sentryReplayIntegrationOptions.
		...sentryReplaySampleRates,
		integrations: [
			Sentry.replayIntegration({
				...sentryReplayIntegrationOptions,
				...replayRedactionOptions,
			}),
			{ name: "RedactSensitiveQueryParams", processEvent: redactEvent },
		],
	});
}

// Instruments App Router client-side navigations for Sentry tracing.
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
